// src/pages/audit-results/components/PremiumGate.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentPricing, subscribeToPricing, getDiscountPercentage, getFormattedPrice } from '../../../constants/pricing';

const GRAD        = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRAD_SHADOW = '0 4px 16px rgba(0,74,173,0.35)';

// ─── SVG lock icon ─────────────────────────────────────────────────────────
function LockIcon() {
  return (
    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path
        d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
        stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Ghost skeleton shapes ─────────────────────────────────────────────────
function GhostContent({ type }) {
  const Line = ({ w = '100%', h = 10 }) => (
    <div style={{ width: w, height: h, background: '#e2e8f0', borderRadius: 5, marginBottom: 8 }} />
  );
  const Bar = ({ w = '75%' }) => (
    <div style={{ width: w, height: 22, background: 'linear-gradient(90deg,#b3cffa,#dce8fd)', borderRadius: 8, marginBottom: 8 }} />
  );
  const GCard = ({ children }) => (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
      {children}
    </div>
  );

  if (type === 'slides') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {[0, 1].map(i => (
        <GCard key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, background: '#cbd5e1', flexShrink: 0 }} />
              <div><Line w={100} h={10} /><Line w={60} h={8} /></div>
            </div>
            <div style={{ width: 56, height: 18, borderRadius: 100, background: '#dce8fd' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><Line w="55%" h={8} /><Line /><Line w="75%" /></div>
            <div><Line w="55%" h={8} /><Line /><Line w="65%" /></div>
          </div>
        </GCard>
      ))}
    </div>
  );

  if (type === 'recs') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[0, 1, 2].map(i => (
        <GCard key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 7 }}>
            <Line w={160} h={10} />
            <div style={{ width: 64, height: 16, borderRadius: 100, background: '#f0f5ff', flexShrink: 0, marginLeft: 8 }} />
          </div>
          <Line /><Line w="80%" />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <div style={{ width: 72, height: 16, borderRadius: 100, background: '#dcf5dc' }} />
            <div style={{ width: 80, height: 16, borderRadius: 100, background: '#fff0d4' }} />
          </div>
        </GCard>
      ))}
    </div>
  );

  if (type === 'benchmark') return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '120px repeat(5,1fr)', gap: 3, marginBottom: 3 }}>
        {['Company','Overall','Narrative','Design','Data','Structure'].map(h => (
          <div key={h} style={{ height: 26, background: '#f1f5f9', borderRadius: 4 }} />
        ))}
      </div>
      {[0,1,2].map(i => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '120px repeat(5,1fr)', gap: 3, marginBottom: 3 }}>
          {Array(6).fill(0).map((_,j) => (
            <div key={j} style={{ height: 28, background: i === 0 ? '#f0f5ff' : '#f8fafc', borderRadius: 4, border: i === 0 ? '1px solid #dce8fd' : 'none' }} />
          ))}
        </div>
      ))}
    </div>
  );

  if (type === 'intel') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
        <GCard><Line w="55%" h={10} /><Bar w="70%" /><Bar w="50%" /></GCard>
        <GCard><Line w="60%" h={10} /><Line /><Line w="70%" /></GCard>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {['Trends','Analysis','Risks'].map(t => (
          <GCard key={t}><Line w="65%" h={9} /><Line /><Line w="70%" /></GCard>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
      {['Outreach','Battle Cards','Documents'].map(t => (
        <GCard key={t}><Line w="60%" h={9} /><Line /><Line w="75%" /></GCard>
      ))}
    </div>
  );
}

// ─── Main PremiumGate ──────────────────────────────────────────────────────
const PremiumGate = ({ locked, sectionId, children }) => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('USD'); // Default to USD, can be made dynamic

  useEffect(() => {
    // Get initial pricing
    const initialPricing = getCurrentPricing();
    setPricing(initialPricing);
    setLoading(false);

    // Subscribe to pricing updates
    const unsubscribe = subscribeToPricing((updatedPricing) => {
      console.log('PremiumGate received pricing update:', updatedPricing);
      setPricing(updatedPricing);
    });

    return () => unsubscribe();
  }, []);

  if (!locked) return <>{children}</>;

  const meta = SECTION_META[sectionId] || {
    icon: <LockIcon />,
    title: 'Premium Feature',
    description: 'This section is available in the full paid report.',
    tags: [],
    ghost: 'artefacts',
  };

  // Get pricing data
  const paidData = pricing?.paid?.[currency] || { original: 299, offer: 75 };
  const originalPrice = paidData.original || 299;
  const offerPrice = paidData.offer || 75;
  const discountPercent = getDiscountPercentage(currency);
  const badgeText = pricing?.paid?.badge || 'LAUNCH OFFER';
  const deliveryTime = pricing?.paid?.deliveryTime || '48-72 hours';
  const bestFor = pricing?.paid?.best || 'Delivered in 48-72 hours';

  // Format prices
  const formattedOriginal = currency === 'INR' 
    ? `₹${originalPrice.toLocaleString('en-IN')}`
    : `$${originalPrice}`;
  
  const formattedOffer = currency === 'INR'
    ? `₹${offerPrice.toLocaleString('en-IN')}`
    : `$${offerPrice}`;

  // Loading state
  if (loading) {
    return (
      <div style={{
        position: 'relative', borderRadius: 16, overflow: 'hidden',
        background: '#f8fafc', border: '1px solid #e2e8f0',
        minHeight: 380, fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <p style={{ color: '#64748b' }}>Loading pricing...</p>
      </div>
    );
  }

  return (
    <div style={{
      position: 'relative', borderRadius: 16, overflow: 'hidden',
      background: '#f8fafc', border: '1px solid #e2e8f0',
      minHeight: 380, fontFamily: 'inherit',
    }}>

      {/* Layer 1 — ghost skeleton: blurred, never drives height */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          filter: 'blur(4px)', WebkitFilter: 'blur(4px)',
          opacity: 0.45, pointerEvents: 'none', userSelect: 'none',
          padding: 16, overflow: 'hidden',
        }}
      >
        <GhostContent type={meta.ghost} />
      </div>

      {/* Layer 2 — soft frosted overlay (NOT a gradient card) */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 16,
        background: 'rgba(248, 250, 252, 0.82)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        zIndex: 1, pointerEvents: 'none',
      }} />

      {/* Layer 3 — content: relative flow, drives height */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center',
        padding: '36px 24px 28px',
        textAlign: 'center',
      }}>

        {/* Lock icon — gradient background */}
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: GRAD,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 14,
          boxShadow: GRAD_SHADOW,
          flexShrink: 0,
        }}>
          {meta.icon}
        </div>

        {/* Title */}
        <h3 style={{
          fontSize: 18, fontWeight: 800,
          color: '#0f172a', letterSpacing: '-0.02em',
          margin: '0 0 6px',
          fontFamily: "'Sansation', sans-serif",
        }}>
          {meta.title}
        </h3>

        {/* Description */}
        <p style={{
          fontSize: 13, color: '#64748b',
          lineHeight: 1.6, margin: '0 0 20px',
          maxWidth: 360,
        }}>
          {meta.description}
        </p>

        {/* CTA button — gradient with dynamic pricing */}
        <button
          onClick={() => navigate('/initial-form-upload-screen')}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            fontSize: 14, fontWeight: 700,
            color: '#ffffff',
            background: GRAD,
            padding: '12px 30px', borderRadius: 100,
            border: 'none', cursor: 'pointer',
            boxShadow: GRAD_SHADOW,
            whiteSpace: 'nowrap',
            transition: 'filter 0.15s, transform 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.filter = 'brightness(1.08)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.filter = '';
            e.currentTarget.style.transform = '';
          }}
        >
          🔓 Unlock Full Report —&nbsp;
          <span style={{ textDecoration: 'line-through', opacity: 0.6, fontWeight: 400 }}>
            {formattedOriginal}
          </span>
          &nbsp;{formattedOffer} →
        </button>

        {/* Discount label */}
        <p style={{
          fontSize: 11, fontWeight: 600,
          color: '#F59E0B', margin: '8px 0 0',
          letterSpacing: '0.02em',
        }}>
          {badgeText} — {discountPercent}% OFF
        </p>

        {/* Feature tags */}
        {meta.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 14 }}>
            {meta.tags.map((tag, i) => (
              <span key={i} style={{
                fontSize: 10, fontWeight: 600,
                color: '#004AAD', background: '#f0f5ff',
                padding: '4px 12px', borderRadius: 100,
                border: '1px solid #dce8fd',
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Sub-label */}
        <p style={{
          fontSize: 10, color: '#94a3b8',
          margin: '10px 0 0', letterSpacing: '0.04em',
        }}>
          AI + Human reviewed · {bestFor}
        </p>
      </div>
    </div>
  );
};

// ─── SECTION_META moved here and now using dynamic data ───────────────────
const SECTION_META = {
  slides: {
    icon: <LockIcon />,
    title: 'Unlock Full Slide-by-Slide Analysis',
    description: "See a detailed breakdown of every slide — what's working, what isn't, and exactly how to fix it.",
    tags: ['Every Slide Scored', 'What Works', "What Doesn't", 'Fix Guide'],
    ghost: 'slides',
  },
  recommendations: {
    icon: <LockIcon />,
    title: 'Unlock Expert Recommendations',
    description: 'AI + Human-powered fixes with priority ranking, implementation guides, and effort estimates — all specific to your deck.',
    tags: ['Priority Ranking', 'Impact Score', 'Effort Estimate', 'Fix Guide per Issue'],
    ghost: 'recs',
  },
  'all-issues': {
    icon: <LockIcon />,
    title: 'Unlock All Identified Issues',
    description: 'See every MEDIUM and LOW priority issue with slide references and detailed fix guides — not just the top 3.',
    tags: ['All HIGH Issues', 'MEDIUM Priority', 'LOW Priority', 'Slide References'],
    ghost: 'recs',
  },
  benchmark: {
    icon: <LockIcon />,
    title: 'Unlock Competitor Benchmarks',
    description: 'See how your deck stacks up against competitors across every dimension with industry averages.',
    tags: ['Competitor Analysis', 'Dimension Scores', 'Industry Average', 'Top Decile'],
    ghost: 'benchmark',
  },
  'industry-trends': {
    icon: <LockIcon />,
    title: 'Unlock Industry Trends & Intelligence',
    description: 'Market-specific trends, risks, and headwinds contextualized to your deck and industry.',
    tags: ['Key Trends', 'Risks & Headwinds', 'Sector Context', 'Market Signals'],
    ghost: 'intel',
  },
  'persona-outputs': {
    icon: <LockIcon />,
    title: 'Unlock Audience Persona Mapping',
    description: 'Understand which investor personas your deck resonates with — and which it misses entirely.',
    tags: ['Investor Personas', 'Resonance Scores', 'Messaging Gaps', 'Tailoring Tips'],
    ghost: 'intel',
  },
  'market-analysis': {
    icon: <LockIcon />,
    title: 'Unlock Market Specific Analysis',
    description: 'TAM/SAM/SOM, competitive landscape, and growth signals — all tied directly to your pitch.',
    tags: ['Market Sizing', 'Competitive Landscape', 'Growth Signals', 'Market Overview'],
    ghost: 'intel',
  },
  'outreach-sequence': {
    icon: <LockIcon />,
    title: 'Unlock Outreach Sequences',
    description: 'Ready-to-use email, WhatsApp, and cold call scripts built directly from your deck.',
    tags: ['Email Sequences', 'WhatsApp Scripts', 'Cold Call Guide'],
    ghost: 'artefacts',
  },
  'battle-cards': {
    icon: <LockIcon />,
    title: 'Unlock Battle Cards',
    description: 'Competitor battle cards with strengths, weaknesses, and win strategies specific to your market.',
    tags: ['Per-Competitor Cards', 'Win Strategy', 'Differentiators', 'Weaknesses'],
    ghost: 'artefacts',
  },
  'key-documents': {
    icon: <LockIcon />,
    title: 'Unlock Key Documents',
    description: 'Ready-to-customize press release, FAQ doc, and GTM brief — auto-generated from your deck.',
    tags: ['Press Release', 'FAQ Doc', 'GTM Brief', 'Key Assets'],
    ghost: 'artefacts',
  },
  'custom-prompt': {
    icon: <LockIcon />,
    title: 'Unlock Custom AI Prompts',
    description: 'Custom deck-rebuild prompts engineered from your audit — paste into ChatGPT, Claude, or Gemini.',
    tags: ['Deck Rebuild Prompts', 'Slide Templates', 'Narrative Scripts', 'Works with Any AI'],
    ghost: 'artefacts',
  },
  versions: {
    icon: <LockIcon />,
    title: 'Unlock Version History',
    description: "Track your deck's progress across revisions and see how your score improves over time.",
    tags: ['Version Tracking', 'Score History', 'Change Log'],
    ghost: 'recs',
  },
  'revised-deck': {
    icon: <LockIcon />,
    title: 'Unlock Revised Deck',
    description: 'A fully redesigned version of your deck with all recommendations applied by our expert team.',
    tags: ['Redesigned Slides', 'Improved Narrative', 'Enhanced Data', 'Expert Polish'],
    ghost: 'artefacts',
  },
};

export default PremiumGate;